import { db } from '../firebase';
import {
  formatDateForApi,
  getTodayInBookingTimeZone,
  daysBetweenCivilDates,
  getBookingWeekday,
} from '../utils/bookingDates';
import { 
  doc, 
  setDoc, 
  getDoc, 
  collection, 
  addDoc, 
  deleteDoc,
  query,
  getDocs,
  serverTimestamp,
  deleteField,
} from 'firebase/firestore';
import {
  DEFAULT_GENERAL_SETTINGS,
  DEFAULT_WEEKLY_SCHEDULE,
  normalizeAvailabilityRecord,
  normalizeGeneralSettings,
} from '../utils/availabilitySettings';

// Collection names
const AVAILABILITY_COLLECTION = 'availability';
const BLOCKED_DATES_COLLECTION = 'blockedDates';

// Default settings (re-exported for callers that need the raw defaults)
const defaultAvailability = DEFAULT_WEEKLY_SCHEDULE;
const defaultGeneralSettings = DEFAULT_GENERAL_SETTINGS;

/** Calendar date as YYYY-MM-DD (matches booking/payment storage). */
export { formatDateForApi as formatDateLocal } from '../utils/bookingDates';

const isValidBlockedDateDoc = (docId, data) =>
  docId !== 'default' && typeof data?.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(data.date);

/** True when the entire day is blocked in admin settings. */
export const isDateFullyBlocked = (date, blockedDates) => {
  const dateString = formatDateForApi(date);
  return blockedDates.some((bd) => bd.date === dateString && bd.type === 'full');
};

// Get availability settings
export const getAvailabilitySettings = async () => {
  try {
    const docRef = doc(db, AVAILABILITY_COLLECTION, 'settings');
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return normalizeAvailabilityRecord(docSnap.data());
    } else {
      // Return default settings without writing to database
      console.log('No availability settings found, using defaults');
      return {
        weeklySchedule: defaultAvailability,
        generalSettings: defaultGeneralSettings
      };
    }
  } catch (error) {
    console.error('Error getting availability settings:', error);
    // Return default settings as fallback
    console.log('Error loading settings, using defaults');
    return {
      weeklySchedule: defaultAvailability,
      generalSettings: defaultGeneralSettings
    };
  }
};

// Save availability settings
export const saveAvailabilitySettings = async (weeklySchedule, generalSettings) => {
  try {
    const docRef = doc(db, AVAILABILITY_COLLECTION, 'settings');
    const normalizedGeneralSettings = normalizeGeneralSettings({ generalSettings });
    await setDoc(
      docRef,
      {
        weeklySchedule,
        generalSettings: normalizedGeneralSettings,
        lastUpdated: serverTimestamp(),
        // Remove legacy flat fields so reads don't fall back to stale values.
        advanceBookingDays: deleteField(),
        blockDaysBeforeBooking: deleteField(),
        blockHoursBeforeBooking: deleteField(),
        preTimeBuffer: deleteField(),
        postTimeBuffer: deleteField(),
      },
      { merge: true }
    );
    return true;
  } catch (error) {
    console.error('Error saving availability settings:', error);
    throw error;
  }
};

// Get blocked dates
export const getBlockedDates = async () => {
  try {
    const q = query(collection(db, BLOCKED_DATES_COLLECTION));
    const querySnapshot = await getDocs(q);
    const blockedDates = [];
    
    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (!isValidBlockedDateDoc(docSnap.id, data)) {
        return;
      }
      blockedDates.push({
        id: docSnap.id,
        ...data,
      });
    });
    
    return blockedDates;
  } catch (error) {
    console.error('Error getting blocked dates:', error);
    // Return empty array as fallback
    console.log('Error loading blocked dates, using empty array');
    return [];
  }
};

// Add blocked date
export const addBlockedDate = async (date, type = 'full', startTime = null, endTime = null, reason = '') => {
  try {
    const docRef = await addDoc(collection(db, BLOCKED_DATES_COLLECTION), {
      date,
      type, // 'full' or 'partial'
      startTime,
      endTime,
      reason,
      createdAt: serverTimestamp()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding blocked date:', error);
    throw error;
  }
};

// Remove blocked date
export const removeBlockedDate = async (id) => {
  try {
    await deleteDoc(doc(db, BLOCKED_DATES_COLLECTION, id));
    return true;
  } catch (error) {
    console.error('Error removing blocked date:', error);
    throw error;
  }
};

// Check if a date/time is available
export const isDateTimeAvailable = (date, time, weeklySchedule, blockedDates, generalSettings = defaultGeneralSettings) => {
  const dayOfWeek = getBookingWeekday(date);
  const schedule = weeklySchedule[dayOfWeek];
  
  // Check if day is enabled
  if (!schedule?.enabled) {
    return false;
  }

  const bookingDateIso = formatDateForApi(date);
  const todayIso = getTodayInBookingTimeZone();
  const daysFromToday = daysBetweenCivilDates(todayIso, bookingDateIso);

  if (daysFromToday == null) {
    return false;
  }

  if (daysFromToday > generalSettings.advanceBookingDays) {
    return false;
  }

  // Block today (0) and tomorrow (1), allow day after tomorrow (2) and beyond
  if (daysFromToday < 2) {
    return false;
  }
  
  // Convert 12-hour format to 24-hour for calculations
  const startTime24 = convertTo24Hour(schedule.start);
  const endTime24 = convertTo24Hour(schedule.end);
  
  // Check if time is within schedule (time is already in 24-hour format)
  const timeHour = parseInt(time.split(':')[0]);
  const timeMinute = parseInt(time.split(':')[1]);
  const timeInMinutes = timeHour * 60 + timeMinute;
  
  const startHour = parseInt(startTime24.split(':')[0]);
  const startMinute = parseInt(startTime24.split(':')[1]);
  const startInMinutes = startHour * 60 + startMinute;
  
  const endHour = parseInt(endTime24.split(':')[0]);
  const endMinute = parseInt(endTime24.split(':')[1]);
  const endInMinutes = endHour * 60 + endMinute;
  
  if (timeInMinutes < startInMinutes || timeInMinutes >= endInMinutes) {
    return false;
  }
  
  // Check blocked dates (use local civil date — toISOString() can shift the day in some timezones)
  const dateString = formatDateForApi(date);
  const blockedDate = blockedDates.find((bd) => bd.date === dateString);
  
  if (blockedDate) {
    if (blockedDate.type === 'full') {
      return false;
    } else if (blockedDate.type === 'partial') {
      // Check if time falls within blocked range
      const blockedStartTime24 = convertTo24Hour(blockedDate.startTime);
      const blockedEndTime24 = convertTo24Hour(blockedDate.endTime);
      
      const blockedStartHour = parseInt(blockedStartTime24.split(':')[0]);
      const blockedStartMinute = parseInt(blockedStartTime24.split(':')[1]);
      const blockedStartInMinutes = blockedStartHour * 60 + blockedStartMinute;
      
      const blockedEndHour = parseInt(blockedEndTime24.split(':')[0]);
      const blockedEndMinute = parseInt(blockedEndTime24.split(':')[1]);
      const blockedEndInMinutes = blockedEndHour * 60 + blockedEndMinute;
      
      if (timeInMinutes >= blockedStartInMinutes && timeInMinutes < blockedEndInMinutes) {
        return false;
      }
    }
  }
  
  return true;
};

// Check if a time slot conflicts with existing bookings (including buffer times)
export const isTimeSlotConflicting = (date, time, existingBookings, generalSettings = defaultGeneralSettings) => {
  const dateString = formatDateForApi(date);
  const dayBookings = existingBookings.filter(booking => booking.date === dateString);
  
  // Convert the requested time to minutes for comparison
  const time24 = convertTo24Hour(time);
  const [requestedHour, requestedMinute] = time24.split(':').map(Number);
  const requestedTimeInMinutes = requestedHour * 60 + requestedMinute;
  
  for (const booking of dayBookings) {
    // Convert booking start time to minutes
    const bookingStart24 = convertTo24Hour(booking.time);
    const [bookingStartHour, bookingStartMinute] = bookingStart24.split(':').map(Number);
    const bookingStartInMinutes = bookingStartHour * 60 + bookingStartMinute;
    
    // Calculate booking end time (assuming 1 hour default, but could be extended)
    const bookingDuration = booking.totalHours || 1;
    const bookingEndInMinutes = bookingStartInMinutes + (bookingDuration * 60);
    
    // Calculate buffer times
    const preBufferInMinutes = generalSettings.preTimeBuffer * 60;
    const postBufferInMinutes = generalSettings.postTimeBuffer * 60;
    
    // Check if requested time conflicts with booking + buffers
    const bookingWithPreBuffer = bookingStartInMinutes - preBufferInMinutes;
    const bookingWithPostBuffer = bookingEndInMinutes + postBufferInMinutes;
    
    if (requestedTimeInMinutes >= bookingWithPreBuffer && requestedTimeInMinutes < bookingWithPostBuffer) {
      return true; // Conflict found
    }
  }
  
  return false; // No conflicts
};

// Helper function to convert 24-hour time to 12-hour format
const convertTo12Hour = (time24) => {
  const [hour, minute] = time24.split(':').map(Number);
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
};

// Helper function to convert 12-hour time to 24-hour format
const convertTo24Hour = (timeValue) => {
  if (!timeValue) return null;

  // Already 24h from <input type="time"> (e.g. "14:00")
  if (!timeValue.includes(' ')) {
    const [hour, minute] = timeValue.split(':').map(Number);
    if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  }

  const [time, period] = timeValue.split(' ');
  const [hour, minute] = time.split(':').map(Number);
  let hour24 = hour;

  if (period === 'PM' && hour !== 12) {
    hour24 = hour + 12;
  } else if (period === 'AM' && hour === 12) {
    hour24 = 0;
  }

  return `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
};

// Get available time slots for a date
export const getAvailableTimeSlots = (date, weeklySchedule, blockedDates, generalSettings = defaultGeneralSettings) => {
  if (isDateFullyBlocked(date, blockedDates)) {
    return [];
  }

  const dayOfWeek = getBookingWeekday(date);
  const schedule = weeklySchedule[dayOfWeek];
  
  if (!schedule?.enabled) {
    return [];
  }
  
  // Convert 12-hour format to 24-hour for calculations
  const startTime24 = convertTo24Hour(schedule.start);
  const endTime24 = convertTo24Hour(schedule.end);
  
  const startHour = parseInt(startTime24.split(':')[0]);
  const startMinute = parseInt(startTime24.split(':')[1]);
  const endHour = parseInt(endTime24.split(':')[0]);
  const endMinute = parseInt(endTime24.split(':')[1]);
  
  const timeSlots = [];
  let currentHour = startHour;
  let currentMinute = startMinute;
  
  // Use 60 minutes as default booking duration
  const bookingDuration = 60;
  
  while (currentHour < endHour || (currentHour === endHour && currentMinute < endMinute)) {
    const time24 = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
    const time12 = convertTo12Hour(time24);
    
    // Check if this time slot is available
    if (isDateTimeAvailable(date, time24, weeklySchedule, blockedDates, generalSettings)) {
      timeSlots.push(time12);
    }
    
    // Move to next time slot
    currentMinute += bookingDuration;
    if (currentMinute >= 60) {
      currentHour += Math.floor(currentMinute / 60);
      currentMinute = currentMinute % 60;
    }
  }
  
  return timeSlots;
};

/** Fetch booked slots for a date via API (works for guests; no customer PII). */
export const fetchBookedSlotsForDate = async (date) => {
  const { getApiBaseUrl } = await import('../config/api');
  const baseUrl = getApiBaseUrl();
  if (!baseUrl) {
    throw new Error('API is not configured');
  }

  const dateString = formatDateForApi(date);
  const url = `${baseUrl.replace(/\/$/, '')}/availability/booked-slots?date=${encodeURIComponent(dateString)}`;
  const response = await fetch(url);

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to load booked slots (${response.status})`);
  }

  const data = await response.json();
  return data.bookings || [];
};
